import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnDestroy,
  OnInit,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import type { TemplateRef } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { AppFunc, DividerComponent, MapPipe } from '@comparit/core';
import { BrandingService } from '@comparit/infrastructure';
import { PageNavigatorService } from '@comparit/shared';
import type { MarkedItem } from '@comparit/shared';
import type { ErgebnisInput } from '../../interfaces/ergebnis-input.model';
import { ResultsBaseService } from '../../services/results-base.service';
import { ResultsSort } from '../../misc/results-sort.class';
import { InvalidEingabenDialogComponent } from '../invalid-eingaben-dialog/invalid-eingaben-dialog.component';
import { ResultsItemErrorComponent } from '../results-item-error/results-item-error.component';
import { ResultsItemSuccessComponent } from '../results-item-success/results-item-success.component';
import { ResultsSortComponent } from '../sort/results-sort.component';

/** Shown until the hub has said how many results to expect. */
const DEFAULT_SKELETONS = 3;

/**
 * The results page of a Sparte. Data comes from the app's `ResultsBaseService`; everything
 * Sparte-specific on screen comes in through inputs: the filter sidebar as a template, the
 * per-result slots as components that take `ergebnis`, routes and labels as strings.
 */
@Component({
  selector: 'ui-results-page',
  imports: [
    AsyncPipe,
    NgTemplateOutlet,
    MatIconModule,
    MatButtonModule,
    MapPipe,
    DividerComponent,
    ResultsSortComponent,
    ResultsItemSuccessComponent,
    ResultsItemErrorComponent,
  ],
  templateUrl: './results-page.component.html',
  styleUrl: './results-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ResultsPageComponent implements OnInit, OnDestroy {
  private readonly navigator = inject(PageNavigatorService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialog = inject(MatDialog);
  private readonly branding = inject(BrandingService);
  protected readonly results: ResultsBaseService<unknown, ErgebnisInput> =
    inject(ResultsBaseService);
  protected readonly AppFunc = AppFunc;

  /** The filter sidebar; rendered in the desktop column and in the drawer on small screens. */
  readonly filterTemplate = input.required<TemplateRef<unknown>>();
  /**
   * The Sparte's detail panel, rendered inside each card's "Details" section with that card's
   * `ErgebnisInput` as the implicit context value. Angular types a `#ref` on an `<ng-template>` as
   * `TemplateRef<any>`, so the declared context documents the contract rather than enforcing it.
   */
  readonly itemDetails = input<TemplateRef<{ $implicit: ErgebnisInput }>>();

  readonly tarifSelected = output<MarkedItem>();

  protected readonly calculatorForm = this.results.calculatorForm;
  private readonly sortBy = toSignal(this.calculatorForm.sortBy$);
  private readonly sortDescending = toSignal(this.calculatorForm.sortDescending$);
  private readonly resultsSort = new ResultsSort();

  protected readonly selectedSort = computed(() => this.sortBy() ?? null);
  protected readonly hasRatings = computed(() => ResultsSort.hasAnyRating(this.results.all()));

  protected readonly pinRecommendation = computed(
    () => !!(this.branding.config()?.features?.pinRecommendation && this.hasRatings())
  );

  protected readonly showAllTarifsUngrouped = computed(
    () => !!this.branding.config()?.features?.showAllTarifsUngrouped
  );

  private readonly successfulResults = computed(() =>
    this.results.all().filter(tarif => tarif.successful)
  );

  protected readonly tarifsToBeShown = computed(() => {
    const ungrouped = this.showAllTarifsUngrouped();
    if (this.pinRecommendation()) {
      return this.resultsSort.sortWithPinnedRecommendation(
        this.successfulResults(),
        this.sortBy(),
        this.sortDescending(),
        ungrouped
      );
    }
    return this.resultsSort.sort(
      this.successfulResults(),
      this.sortBy(),
      this.sortDescending(),
      ungrouped
    );
  });

  protected readonly bestTarifKombinationId = computed(() =>
    ResultsSort.findBestTarifId(this.tarifsToBeShown())
  );

  protected readonly tarifsWithErrors = computed(() =>
    this.results.all().filter(tarif => !tarif.successful)
  );

  protected readonly expectedErgebnisse = computed(
    () => this.results.berechnungResponse()?.anzahlTarife ?? 0
  );

  /** One placeholder per result still on its way. */
  protected readonly skeletons = computed(() => {
    const expected = this.results.berechnungResponse()?.anzahlTarife ?? DEFAULT_SKELETONS;
    return Array.from({ length: Math.max(0, expected - this.results.all().length) }, (_, i) => i);
  });

  protected readonly invalidInputs = signal(false);

  ngOnInit(): void {
    this.results.enterCalculatorPage();

    this.results
      .formInvalid$()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(invalid => {
        this.invalidInputs.set(invalid);
        if (invalid) {
          this.dialog.open(InvalidEingabenDialogComponent);
        }
      });

    this.calculatorForm.compare$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(compare => {
      const { backUrl, forwardUrl } = this.results;
      this.navigator.setup.set({
        backLink: { url: backUrl, disabled: false },
        info: 'Ergebnisse',
        forward: compare.length && forwardUrl ? { url: forwardUrl } : undefined,
      });
    });
  }

  ngOnDestroy(): void {
    this.results.leaveCalculatorPage();
  }

  protected openFilter(): void {
    AppFunc.rightSidebarContent$.next(this.filterTemplate());
    AppFunc.toggleRightSidebar$.next(true);
  }

  protected closeFilter(): void {
    AppFunc.toggleRightSidebar$.next(false);
  }
}
